<template>
  <div class="page-body">
    <div class="container-fluid">
      <Loader :loading="isLoading" />
      <!-- <loader-component v-if="isLoading" :loading="isLoading" /> -->
      <div class="edit-profile">
        <div class="row">
          <div class="col-12 col-xl-12">
            <div class="card">
              <div class="card-header">
                <div class="text-neutral-500 text-xl font-medium font-['Poppins'] leading-normal">
                  {{ companyId && companyId !== '' ? "Editar Compañía" : "Crear Compañía" }}
                </div>
              </div>

              <!-- Tabs Navigation -->
              <div class="card-tabs">
                <div class="flex border-b">
                  <button 
                    @click="activeTab = 'general'" 
                    :class="{'border-b-2 border-blue-500 text-blue-500': activeTab === 'general'}"
                    class="px-4 py-2 text-neutral-500 font-medium"
                  >
                    General
                  </button>
                  <button 
                    @click="activeTab = 'ubicacion'" 
                    :class="{'border-b-2 border-blue-500 text-blue-500': activeTab === 'ubicacion', 'opacity-50 cursor-not-allowed': !isSocialTabEnabled}"
                    class="px-4 py-2 text-neutral-500 font-medium"
                    :disabled="!isSocialTabEnabled"
                  >
                    Ubicación
                  </button>
                  <button 
                    @click="activeTab = 'social'" 
                    :class="{'border-b-2 border-blue-500 text-blue-500': activeTab === 'social', 'opacity-50 cursor-not-allowed': !isSocialTabEnabled}"
                    class="px-4 py-2 text-neutral-500 font-medium"
                    :disabled="!isSocialTabEnabled"
                  >
                    Redes Sociales
                  </button>
                </div>
              </div>

              <div class="card-body" style="margin-top: 20px;">
                <div v-if="activeTab === 'general'">
                  <form @submit.prevent="submitForm">
                    <!-- Primera fila: Imagen, Nombre y Email -->
                    <div class="flex flex-col md:flex-row gap-8 mb-4">
                      <!-- Columna de la imagen -->
                      <div class="w-full md:w-1/3 flex flex-col gap-4 items-center">
                        <div class="w-full relative image-container">
                          <input 
                            type="file" 
                            accept="image/*" 
                            @change="loadImage" 
                            class="absolute inset-0 opacity-0 cursor-pointer z-10"
                            :key="imageInputKey"
                          />
                          <div class="w-full h-[200px] flex items-center justify-center rounded-2xl border-2 border-[#ff4265] overflow-hidden relative">
                            <div 
                              v-if="!form.img_src" 
                              class="text-neutral-500 text-base font-light font-['Poppins'] leading-normal text-center"
                            >
                              Arrastra aquí tu imagen (342 x342)
                            </div>
                            <img 
                              v-if="form.img_src" 
                              :src="form.img_src" 
                              alt="Vista previa de la imagen" 
                              class="absolute inset-0 w-full h-full object-cover pointer-events-none" 
                            />
                            <button 
                              v-if="form.img_src"
                              @click.stop="removeImage"
                              class="absolute top-2 right-2 bg-white text-red-500 rounded-full w-6 h-6 flex items-center justify-center shadow-md hover:bg-red-500 hover:text-white transition duration-300 z-20"
                              title="Quitar imagen"
                            >
                              &times;
                            </button>
                          </div>
                        </div>
                      </div>

                      <!-- Columna de campos (Nombre y Email) -->
                      <div class="w-full md:w-2/3 flex flex-col gap-4">
                        <!-- Nombre -->
                        <div class="w-full flex-col justify-start items-start gap-1.5 inline-flex">
                          <label class="text-neutral-500 text-sm font-medium font-['Poppins'] leading-normal">Nombre</label>
                          <input 
                            type="text" 
                            v-model="form.name"
                            class="self-stretch px-4 py-[9px] bg-white rounded-md border border-[#dee2e6] text-neutral-500 text-sm font-light font-['Poppins'] leading-normal"
                            placeholder="Nombre de la compañía" 
                          />
                          <!-- Alerta para Nombre -->
                          <div v-if="showNameAlert" class="required-alert">
                            <div class="yellow-box">!</div>
                            <span>Completa este campo</span>
                          </div>
                        </div>

                        <!-- Email -->
                        <div class="w-full flex-col justify-start items-start gap-1.5 inline-flex">
                          <label class="text-neutral-500 text-sm font-medium font-['Poppins'] leading-normal">Email</label>
                          <input 
                            type="email" 
                            v-model="form.email"
                            class="self-stretch px-4 py-[9px] bg-white rounded-md border border-[#dee2e6] text-neutral-500 text-sm font-light font-['Poppins'] leading-normal"
                            placeholder="Ingresa el email" 
                            pattern="[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$"
                          />
                          <!-- Alerta para Email -->
                          <div v-if="showEmailAlert" class="required-alert">
                            <div class="yellow-box">!</div>
                            <span>Completa este campo</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Segunda fila: Teléfono, Sitio Web y Descripción -->
                    <div class="flex flex-col md:flex-row gap-8 mb-4">
                      <!-- Teléfono -->
                      <div class="w-full md:w-1/3 flex flex-col gap-4">
                        <div class="w-full flex-col justify-start items-start gap-1.5 inline-flex">
                          <label class="text-neutral-500 text-sm font-medium font-['Poppins'] leading-normal">Teléfono</label>
                          <input 
                            type="tel" 
                            v-model="form.phone"
                            class="self-stretch px-4 py-[9px] bg-white rounded-md border border-[#dee2e6] text-neutral-500 text-sm font-light font-['Poppins'] leading-normal"
                            placeholder="Teléfono de la compañía" 
                            pattern="[\d\s\+\-\(\)]{10,}"
                            title="Ingrese un número de teléfono válido (mínimo 10 dígitos)"
                          />
                          <!-- Alerta para telefono -->
                          <div v-if="showPhoneAlert" class="required-alert">
                            <div class="yellow-box">!</div>
                            <span>Completa este campo</span>
                          </div>
                        </div>
                      </div>

                      <!-- Sitio Web -->
                      <div class="w-full md:w-1/3 flex flex-col gap-4">
                        <div class="w-full flex-col justify-start items-start gap-1.5 inline-flex">
                          <label class="text-neutral-500 text-sm font-medium font-['Poppins'] leading-normal">Sitio Web</label>
                          <input 
                            type="url" 
                            v-model="form.website"
                            class="self-stretch px-4 py-[9px] bg-white rounded-md border border-[#dee2e6] text-neutral-500 text-sm font-light font-['Poppins'] leading-normal"
                            placeholder="Ingresa el sitio web" 
                            pattern="https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)"
                            title="Ingrese una URL válida comenzando con http:// o https://"
                          />
                        </div>
                      </div>

                      <!-- Descripción -->
                      <div class="w-full md:w-1/3 flex flex-col gap-4">
                        <label class="text-neutral-500 text-sm font-medium font-['Poppins'] leading-normal">Descripción</label>
                        <ckeditor
                          v-model="form.description"
                          :editor="editor"
                          :config="editorConfig"
                          class="h-40"
                        ></ckeditor>
                      </div>
                    </div>

                    <!-- Botones -->
                    <div class="form-footer text-end mt-4">
                      <button type="button" class="custom-cancel-btn" @click="cancelChanges">
                        <span class="custom-cancel-btn-text">Cancelar</span>
                      </button>
                      <button type="submit" class="custom-create-btn" :disabled="isSubmitting"
                        :class="{ 'disabled-button': isSubmitting }">
                        <span class="custom-create-btn-text flex justify-center items-center w-full">{{ isSubmitting ? 'Guardando...' : 'Guardar' }}</span>
                      </button>
                    </div>
                  </form>
                </div>
                <div v-if="activeTab === 'ubicacion'">
                  <FormLocationComponent
                    :property-id="companyId" 
                    :resp="data_company" 
                    Type="company" 
                  />
                </div>
                <div v-if="activeTab === 'social'">
                  <social-networks-form 
                      :company-id="companyId" 
                      @social-data-updated="handleSocialDataUpdate"
                  ></social-networks-form>
                </div>
              </div>
              <!-- Mensaje de éxito -->
              <div v-if="successMessage" class="user-alert">
                <div class="user-alert-content">
                  <div class="user-alert-text">
                    {{ successMessage }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
  
  <script>
  import axios from "axios";
  import { ref, computed, watch, onMounted } from "vue";
  import { useStore } from "vuex";
  import { useRouter } from "vue-router";
  import { useRoute } from "vue-router";
  import SocialNetworksForm from '@/components/company/NetworksCompanyComponent.vue'; // Import the new component
  import Loader from '@/components/loaderComponent.vue';
  import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
  import FormLocationComponent from "../advisor/FormLocationComponent.vue";


  export default {
    components: {
      SocialNetworksForm,
      FormLocationComponent,
      Loader
    },
    setup() {
      const resizeObserverLoopErr = () => {
      const originalOnError = window.onerror;
        window.onerror = function(message, source, lineno, colno, error) {
          if (message.includes('ResizeObserver') || 
            (error && error.message && error.message.includes('ResizeObserver'))) {
            return true; // Suprime el error
          }
          return originalOnError ? originalOnError.apply(this, arguments) : false;
        };
      };
      let data_company = ref({});
      const isLoading = ref(true); // Inicializamos isLoading
      const activeTab = ref('general'); // Default active tab
      const store = useStore();
      const session = computed(() => store.getters.getUserSession);
      const route = useRoute();
      const companyId = computed(() => route.params.id);  // Obtener el ID del rol desde la URL
      const imageInputKey = ref(0); // Inicializa la clave
      const existingImageSrc = ref(null);
      const placeholderVisible = ref(true);
      const isSubmitted = ref(false);
      const isSubmitting = ref(false); 

      // Variables para controlar las alertas
      const showNameAlert = ref(false);
      const showEmailAlert = ref(false);
      const showPhoneAlert = ref(false);
  
      const editor            = ref(ClassicEditor);
      const editorConfig = {
      toolbar: [
        "heading",
        "|",
        "bold",
        "italic",
        "underline",
        "strikethrough",
        "|",
        "link",
        "blockQuote",
        "|",
        "undo",
        "redo",
        "|",
        "numberedList",
        "bulletedList",
        "|",
        "alignment",
        "indent",
        "outdent",
        "|",
        "insertHR",
        "code",
        "codeBlock",
        "removeFormat",
        "|",
        "fontSize",
        "fontFamily",
        "fontColor",
        "backgroundColor",
        "highlight",
      ],
      colorButton_enableMore: true,
      resizeObserver: false,
      contentsCss: [
        "body { background-color: white !important; color: black !important; }"
      ]
    };

      const form = ref({
        id: "",
        name: "",
        description: "",
        email: "",       
        phone: "",     
        website: "",      
        slug: "",         
        img_src: null,        
      });
      const imageInput = ref(null);
      const successMessage = ref("");
      const router = useRouter();

  // Función para validar campos
  const validateField = (field) => {
  switch(field) {
    case 'name':
      return form.value.name.trim() !== '';
    case 'email':
      return form.value.email.trim() !== '';
    case 'phone':
      return form.value.phone.trim() !== '';
    default:
      return true;
    }
  };
  
      // Función para cargar los datos del rol cuando estamos en modo de edición
      const loadRoleData = async () => {
  try {
    isLoading.value = true;
    
    if (!companyId.value) {
      // Caso para creación nueva (sin ID)
      form.value = { 
        name: "", 
        description: "", 
        email: "", 
        phone: "", 
        website: "", 
        img_src: null 
      };
      return;
    }

    const token = session.value.token_api;
    if (!token) {
      console.error("No se ha encontrado el token de autenticación.");
      return;
    }

    const options = {
      headers: { Authorization: `Bearer ${token}` }
    };

    const response = await axios.get(
      `${process.env.VUE_APP_API_BASE_URL}/company-form/${companyId.value}`, 
      options
    );

    if (response.status === 200) {
      data_company.value = response; 
      form.value = {
        id: response.data.resp.company.id,
        name: response.data.resp.company.name,
        description: response.data.resp.company.description || '',
        email: response.data.resp.company.email,
        phone: response.data.resp.company.phone,
        website: response.data.resp.company.website,
        img_src: response.data.resp.company.logo,
        slug: response.data.resp.company.slug
      };
      existingImageSrc.value = response.data.resp.company.logo;
    }
  } catch (error) {
    console.error("Error al cargar los datos:", error);
    // Opcional: Mostrar mensaje de error al usuario
  } finally {
    isLoading.value = false;
  }
};
      const removeImage = () => {
        form.value.img_src = null; // Eliminamos la imagen
        existingImageSrc.value = null;
        placeholderVisible.value = true; // Mostramos el marcador de posición nuevamente

        // Restablecemos el valor del input para permitir cargar la misma imagen
        if (imageInput.value) {
          imageInput.value.value = null; // Restablece el input de archivo
        }

        // Cambia la clave para forzar la recreación del input
        imageInputKey.value += 1; // Incrementa la clave
        console.log('Removing image', { current: form.value.img_src, existing: existingImageSrc.value });
      };
      const handleSocialDataUpdate = (socialData) => {
        // You can add logic here to handle social data if needed
        console.log('Social data updated:', socialData);
      };
      // Llamar a la función cuando el componente se monta
      onMounted(async () => {
        resizeObserverLoopErr();
        await loadRoleData();
      });

      const updateDescription = (event) => {
          form.value.description = event.target.value;
      };
      const isSocialTabEnabled = computed(() => {
        return companyId.value !== undefined && companyId.value !== null && companyId.value !== 0;
      });
      
      // Función para guardar o actualizar la compañía
      const submitForm = async () => {
       isSubmitting.value = true;
  
      // Resetear todas las alertas
      showNameAlert.value = false;
      showEmailAlert.value = false;
      showPhoneAlert.value = false;

      // Validar campos
      const isNameValid = validateField('name');
      const isEmailValid = validateField('email');
      const isPhoneValid = validateField('phone');

      // Mostrar alertas solo si la validación falla
      showNameAlert.value = !isNameValid;
      showEmailAlert.value = !isEmailValid;
      showPhoneAlert.value = !isPhoneValid;
      

      // Si alguna validación falla, detener el envío
      if (!isNameValid || !isEmailValid || 
        !isPhoneValid) {
        isSubmitting.value = false;
        return;
      }

        try {
          const token = session.value.token_api;
          if (!token) {
            console.error("No se ha encontrado el token de autenticación.");
            isSubmitting.value = false;
            return;
          }

          const options = {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          };

          const endpoint = companyId.value
            ? `${process.env.VUE_APP_API_BASE_URL}/company-update/${companyId.value}`
            : `${process.env.VUE_APP_API_BASE_URL}/company-update`;

          const method = companyId.value ? "post" : "post";

          // Aquí construimos el objeto dataToSend
          const dataToSend = { 
            ...form.value,
            // Si img_src es null y existingImageSrc tenía valor, estamos eliminando la imagen
            ...(form.value.img_src === null && existingImageSrc.value ? { img_src: null } : {}),
            // Solo incluir img_src si se ha modificado
            ...(form.value.img_src && form.value.img_src !== existingImageSrc.value ? { img_src: form.value.img_src } : {})
          };
          console.log(dataToSend)
          const response = await axios[method](endpoint, dataToSend, options);

          if (response.status === 200 || response.status === 201) {
            successMessage.value = companyId.value
              ? "Compañía actualizada con éxito."
              : "Compañía creada con éxito.";

            if (!companyId.value) {
              form.value = {
                name: "", email: "", phone: "", website: ""
              };
            }

            setTimeout(() => {
              successMessage.value = "";
              if (!companyId.value) {
                const newCompanyId = response.data.company_id;
                router.push({ 
                  name: 'company-form-edit',
                  params: { id: newCompanyId }
                });
              } else {
                router.push({ name: "company-list" });
              }
            }, 2000);
          }
        } catch (error) {
          console.error("Error al guardar la compañía:", error);
          if (error.response) {
            console.error("Error de servidor:", error.response.data);
            if (error.response.data.message) {
              successMessage.value = error.response.data.message; // Muestra el mensaje de error del servidor
            }
          }
        }finally {
        isSubmitting.value = false;
        isSubmitted.value = false;}
      };
      watch(activeTab, async (newTab) => {
        if (newTab === 'general' || newTab === 'social') {
          await loadRoleData();
        }
      });
      watch(
        () => route.params.id,
        (newId, oldId) => {
          // Verifica si el nuevo valor es diferente de null y si era null previamente
          if (newId !== oldId && newId !== null) {
            loadRoleData(); // Cargar los datos cuando cambie el ID de null a un valor
          }
        },
        { immediate: true } // Para ejecutar la primera vez también
      );

      // Método para cargar nueva imagen
      const loadImage = (event) => {
        const file = event.target.files[0];

        if (file) {
          const reader = new FileReader();
          reader.onload = (e) => {
            form.value.img_src = e.target.result; // Actualiza la imagen en base64
          };
          reader.readAsDataURL(file); // Convierte el archivo en una URL de datos
        }
      };
      const cancelChanges = () => {
                router.push({ name: "company-list" });
      }

return {
            Loader,
            form,
            submitForm,
            loadImage,
            data_company,
            updateDescription,
            activeTab,
            companyId,
            handleSocialDataUpdate,
            existingImageSrc,
            successMessage,
            editorConfig,
            removeImage,
            editor,
            isSubmitting,
            isSubmitted,
            showNameAlert,
            showEmailAlert,
            showPhoneAlert,
            validateField,
            isSocialTabEnabled,
            imageInput,
            imageInputKey,
            placeholderVisible,
            cancelChanges,
            isLoading
        };
    }
};
  </script>

<style>
  .ck-editor__editable_inline {
    background-color: rgb(253, 253, 253) !important;
    color: rgb(9, 9, 9) !important;
  }

  .disabled-button {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .required-alert {
    display: flex;
    align-items: center;
    background-color: #fff;
    color: #333;
    padding: 10px;
    border: 1px solid #ccc;
    border-radius: 5px;
    margin-top: 5px;
    position: relative;
    max-width: 300px;
    word-wrap: break-word;
  }

  .required-alert .yellow-box {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 24px;
    height: 24px;
    background-color: #e59035;
    margin-right: 10px;
    border: 1px solid #e59035;
    border-radius: 4px;
    color: #fff;
    font-weight: bold;
    font-size: 16px;
    line-height: 1;
  }

  .required-alert::after {
    content: "";
    position: absolute;
    top: -10px;
    left: 20px;
    width: 0;
    height: 0;
    border-left: 10px solid transparent;
    border-right: 10px solid transparent;
    border-bottom: 10px solid #fff;
    z-index: 1;
  }

  .required-alert::before {
    content: "";
    position: absolute;
    top: -11px;
    left: 19px;
    width: 0;
    height: 0;
    border-left: 11px solid transparent;
    border-right: 11px solid transparent;
    border-bottom: 11px solid #ccc;
    z-index: 0;
  }

  .custom-cancel-btn {
  display: inline-flex;
  padding: 8px 16px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  border-radius: 4px;
  background: #4B5563; /* Gris oscuro */
  border: 1px solid #4B5563;
  color: white;
  cursor: pointer;
  margin-right: 10px; /* Espacio entre botones */
  transition: all 0.3s ease;
}

.custom-cancel-btn-text {
  color: #FFF;
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
}

.form-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

@media (max-width: 767px) {
    .card-body {
      padding: 15px;
    }
    
    .flex-col-mobile {
      flex-direction: column;
    }
    
    .w-full-mobile {
      width: 100% !important;
    }
    
    .space-x-4 {
      margin-left: 0;
      margin-right: 0;
    }
    
    .gap-8 {
      gap: 1rem;
    }
    
    .ck-editor__editable_inline {
      min-height: 200px;
    }
    
    .form-footer {
      flex-direction: column;
      gap: 10px;
    }
    
    .custom-cancel-btn, 
    .custom-create-btn {
      width: 100%;
      margin-right: 0;
      margin-bottom: 10px;
      text-align: center;
      justify-content: center;
      align-items: center;
    }
  }

  @media (min-width: 768px) {
    .form-footer {
      justify-content: flex-end;
      gap: 10px;
    }
  }
  .image-container {
  width: 100%;
  max-width: 400px;
  margin: 0 auto;
}

@media (min-width: 768px) {
  .image-container {
    width: 100%;
    max-width: 500px;
  }
  
  
  .image-container img {
    object-fit: contain;
    max-height: 300px;
    width: 100%;
    height: 100%;
  }
}

@media (min-width: 1024px) {
  .image-container {
    max-width: 600px;
  }
}

</style>